import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { C } from '../../constants/colors.js';
import { LEVELS } from '../../constants/levels.js';
import { QUIZ_Q } from '../../constants/content.js';
import { LEVEL_CONFIG, getEhriPhase, getLevelName } from '../../config/levelConfig.js';
import { Btn } from '../ui/Btn.jsx';
import { SD } from '../ui/StepDots.jsx';
import { useTTS } from '../../hooks/useTTS.js';
import { fadeIn, popIn, slideUp, tapBounce } from '../../lib/motionPresets.js';

const ENCOURAGEMENTS = ['Keep going!', "You're doing great!", 'Almost there!'];

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function getAgeQuizConfig(age, conf) {
  if (age >= 4 && age <= 5) {
    return {
      band: '4-5',
      minItems: 5,
      maxItems: 10,
      minTapTarget: 64,
      requiresNarration: true,
      minLevel: 2,
      maxLevel: 6,
      startLevel: conf === 'loves_it' ? 4 : 3,
      instruction: 'Tap the answer that sounds right.'
    };
  }
  if (age >= 6 && age <= 8) {
    return {
      band: '6-8',
      minItems: 5,
      maxItems: 14,
      minTapTarget: 56,
      requiresNarration: false,
      minLevel: 4,
      maxLevel: 10,
      startLevel: conf === 'struggles' ? 4 : conf === 'loves_it' ? 8 : 6,
      instruction: 'Pick the best answer.'
    };
  }
  if (age >= 9 && age <= 12) {
    return {
      band: '9-12',
      minItems: 5,
      maxItems: 18,
      minTapTarget: 56,
      requiresNarration: false,
      minLevel: 8,
      maxLevel: 13,
      startLevel: conf === 'struggles' ? 8 : conf === 'loves_it' ? 12 : 10,
      instruction: 'Read each prompt and choose the best answer.'
    };
  }
  return {
    band: 'unknown',
    minItems: 5,
    maxItems: 14,
    minTapTarget: 56,
    requiresNarration: false,
    minLevel: 2,
    maxLevel: 13,
    startLevel: conf === 'struggles' ? 3 : conf === 'loves_it' ? 8 : 5,
    instruction: 'Pick the best answer.'
  };
}

function findNearestUnusedIndex(bank, usedSet, targetIdx) {
  if (!bank.length) return -1;
  const clamped = clamp(targetIdx, 0, bank.length - 1);
  if (!usedSet.has(bank[clamped].id)) return clamped;
  for (let d = 1; d < bank.length; d++) {
    const up = clamped + d;
    const dn = clamped - d;
    if (up < bank.length && !usedSet.has(bank[up].id)) return up;
    if (dn >= 0 && !usedSet.has(bank[dn].id)) return dn;
  }
  return -1;
}

function findNextAdaptiveIndex(bank, usedSet, currentIdx, direction, jump) {
  const target = currentIdx + direction * jump;
  const direct = findNearestUnusedIndex(bank, usedSet, target);
  if (direct !== -1) return direct;
  return findNearestUnusedIndex(bank, usedSet, currentIdx + direction);
}

function mapToConfiguredLevel(level) {
  const configured = LEVEL_CONFIG.map((l) => l.level).sort((a, b) => a - b);
  if (!configured.length) return clamp(level, 1, 15);
  if (configured.includes(level)) return level;
  let best = configured[0];
  let bestDist = Math.abs(level - best);
  for (const lv of configured) {
    const dist = Math.abs(level - lv);
    if (dist < bestDist) {
      best = lv;
      bestDist = dist;
    }
  }
  return best;
}

function calculatePlacementLevel(responses, fallbackLevel = 1) {
  const scored = responses.filter((r) => !r.guessFlag);
  if (!scored.length) return mapToConfiguredLevel(fallbackLevel);

  const byLevel = {};
  for (const response of scored) {
    if (!byLevel[response.level]) byLevel[response.level] = { attempted: 0, correct: 0 };
    byLevel[response.level].attempted += 1;
    byLevel[response.level].correct += response.correct ? 1 : 0;
  }

  const consistentLevels = Object.entries(byLevel)
    .map(([level, stats]) => ({ level: Number(level), ...stats }))
    .filter((stats) => stats.correct > 0 && stats.correct / stats.attempted >= 0.6)
    .map((stats) => stats.level)
    .sort((a, b) => b - a);

  if (consistentLevels.length) return mapToConfiguredLevel(consistentLevels[0]);

  const highestCorrect = scored
    .filter((r) => r.correct)
    .map((r) => r.level)
    .sort((a, b) => b - a)[0];

  return mapToConfiguredLevel(highestCorrect || fallbackLevel);
}

export function Quiz({ name, conf, age, onComplete, onCancel }) {
  const config = useMemo(() => getAgeQuizConfig(age, conf), [age, conf]);
  const startedAtRef = useRef(Date.now());
  const speakRef = useRef(null);
  const promptTimerRef = useRef(null);
  const questionShownAtRef = useRef(Date.now());
  const { speak, stop } = useTTS();

  const bank = useMemo(() => {
    const scoped = QUIZ_Q
      .filter((q) => q.level >= config.minLevel && q.level <= config.maxLevel)
      .map((q) => ({ ...q, id: `${q.level}:${q.prompt}` }));

    // TODO: Add more age-band-specific placement items.
    // Current item bank is limited, so older bands can exhaust before max-items.
    if (scoped.length >= config.minItems) return scoped;

    return QUIZ_Q.map((q) => ({ ...q, id: `${q.level}:${q.prompt}` }))
      .sort((a, b) => a.level - b.level);
  }, [config.minItems, config.minLevel, config.maxLevel]);

  const initialIndex = useMemo(() => {
    if (!bank.length) return 0;
    const nearest = bank.reduce((best, item, idx) => {
      const bestDiff = Math.abs(best.level - config.startLevel);
      const currDiff = Math.abs(item.level - config.startLevel);
      return currDiff < bestDiff ? { level: item.level, idx } : best;
    }, { level: bank[0].level, idx: 0 });
    return nearest.idx;
  }, [bank, config.startLevel]);

  const [currentIdx, setCurrentIdx] = useState(initialIndex);
  const [responses, setResponses] = useState([]);
  const [usedIds, setUsedIds] = useState(() => new Set());
  const [selected, setSelected] = useState(null);
  const [done, setDone] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);
  const [placedLevel, setPlacedLevel] = useState(null);
  const [correctStreak, setCorrectStreak] = useState(0);
  const [wrongStreak, setWrongStreak] = useState(0);
  const [guessStreak, setGuessStreak] = useState(0);
  const [encouragement, setEncouragement] = useState('');
  const [guessPrompt, setGuessPrompt] = useState('');
  const [questionSeed, setQuestionSeed] = useState(0);
  const [lastCorrectLevel, setLastCorrectLevel] = useState(1);

  const currentQuestion = bank[currentIdx] || bank[0];
  const answeredCount = responses.length;
  const totalDots = 6;
  const progressDots = clamp(Math.ceil((answeredCount / Math.max(config.maxItems, 1)) * totalDots), 0, totalDots);

  useEffect(() => {
    return () => {
      if (speakRef.current) window.clearTimeout(speakRef.current);
      if (promptTimerRef.current) window.clearTimeout(promptTimerRef.current);
      stop();
    };
  }, [stop]);

  useEffect(() => {
    questionShownAtRef.current = Date.now();
    setSelected(null);

    if (!config.requiresNarration || !currentQuestion) return;
    stop();
    const narration = `Listen carefully. ${currentQuestion.prompt}. ${currentQuestion.options
      .map((opt, i) => `Option ${i + 1}: ${opt}.`)
      .join(' ')}`;
    speakRef.current = window.setTimeout(() => {
      speak(narration, { rate: 0.95, pitch: 1.0 });
    }, 120);
  }, [config.requiresNarration, currentQuestion?.id, questionSeed, speak, stop]);

  const getQuizMeta = (finalLevel) => {
    const byLevel = {};
    for (const answer of responses) {
      if (!byLevel[answer.level]) byLevel[answer.level] = { attempted: 0, correct: 0, guessed: 0 };
      byLevel[answer.level].attempted += 1;
      byLevel[answer.level].correct += answer.correct ? 1 : 0;
      byLevel[answer.level].guessed += answer.guessFlag ? 1 : 0;
    }
    return {
      durationSeconds: Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000)),
      rawSkillScores: {
        responses,
        byLevel,
        suggestedLevel: placedLevel,
        confirmedLevel: finalLevel,
        ageBand: config.band,
        minItems: config.minItems,
        maxItems: config.maxItems
      }
    };
  };

  const finishQuiz = (nextResponses, fallbackLevel) => {
    const level = calculatePlacementLevel(nextResponses, fallbackLevel);
    setPlacedLevel(level);
    setDone(true);
  };

  const handleAnswer = (optionIndex) => {
    if (!currentQuestion || selected !== null) return;
    setSelected(optionIndex);
    const responseMs = Date.now() - questionShownAtRef.current;
    const guessFlag = responseMs < 300;
    const correct = optionIndex === currentQuestion.answer;
    const response = {
      id: currentQuestion.id,
      level: currentQuestion.level,
      correct,
      guessFlag,
      responseMs
    };

    const nextResponses = [...responses, response];
    const nextUsed = new Set(usedIds);
    nextUsed.add(currentQuestion.id);
    const nextAnsweredCount = nextResponses.length;

    let nextCorrectStreak = correctStreak;
    let nextWrongStreak = wrongStreak;
    let nextLastCorrect = lastCorrectLevel;
    let nextGuessStreak = guessFlag ? guessStreak + 1 : 0;

    if (!guessFlag) {
      if (correct) {
        nextCorrectStreak += 1;
        nextWrongStreak = 0;
        nextLastCorrect = currentQuestion.level;
      } else {
        nextWrongStreak += 1;
        nextCorrectStreak = 0;
      }
    }

    setResponses(nextResponses);
    setUsedIds(nextUsed);
    setCorrectStreak(nextCorrectStreak);
    setWrongStreak(nextWrongStreak);
    setLastCorrectLevel(nextLastCorrect);
    setGuessStreak(nextGuessStreak);

    if (nextAnsweredCount % 3 === 0) {
      const msg = ENCOURAGEMENTS[Math.floor(nextAnsweredCount / 3 - 1) % ENCOURAGEMENTS.length];
      setEncouragement(msg);
      if (promptTimerRef.current) window.clearTimeout(promptTimerRef.current);
      promptTimerRef.current = window.setTimeout(() => setEncouragement(''), 1400);
    }

    if (nextGuessStreak >= 3) {
      const msg = 'Take your time! Finley wants to find the best adventure for you.';
      setGuessPrompt(msg);
      if (config.requiresNarration) {
        stop();
        speak(msg, { rate: 0.95, pitch: 1.0 });
      }
      if (promptTimerRef.current) window.clearTimeout(promptTimerRef.current);
      promptTimerRef.current = window.setTimeout(() => setGuessPrompt(''), 2200);
    }

    const hitMaxItems = nextAnsweredCount >= config.maxItems;
    const shouldStopForStruggle = !guessFlag && nextAnsweredCount >= config.minItems && nextWrongStreak >= 3;
    if (hitMaxItems || shouldStopForStruggle) {
      window.setTimeout(() => finishQuiz(nextResponses, nextLastCorrect), 250);
      return;
    }

    let direction = 1;
    let jump = 1;
    if (!guessFlag) {
      if (correct) {
        jump = nextCorrectStreak >= 3 ? 3 : nextCorrectStreak >= 2 ? 2 : 1;
      } else {
        direction = -1;
        jump = nextWrongStreak >= 2 ? 2 : 1;
      }
    }

    const nextIndex = findNextAdaptiveIndex(bank, nextUsed, currentIdx, direction, jump);
    if (nextIndex === -1) {
      window.setTimeout(() => finishQuiz(nextResponses, nextLastCorrect), 250);
      return;
    }

    window.setTimeout(() => {
      setCurrentIdx(nextIndex);
      setQuestionSeed((s) => s + 1);
    }, 250);
  };

  if (confirmStep && placedLevel) {
    const lv = LEVELS[placedLevel] || LEVELS[1];
    const levelName = getLevelName(placedLevel);
    const ehriPhase = getEhriPhase(placedLevel);
    return (
      <motion.div {...popIn} className="px-6 py-8 text-center">
        <div className="mb-3 text-5xl">👋</div>
        <h2 className="mb-2 font-finley-display text-2xl text-finley-text">Parent Check</h2>
        <p className="mb-4 text-base text-finley-text-light">
          Based on the quiz, <strong className="text-finley-primary">{name}</strong> is reading at:
        </p>
        <div className="mb-5 inline-block rounded-finley-lg border-2 px-7 py-3 shadow-finley-sm" style={{ backgroundColor: `${lv.color}15`, borderColor: `${lv.color}44` }}>
          <div className="font-finley-display text-3xl" style={{ color: lv.color }}>{levelName}</div>
          <div className="text-sm text-finley-text-light">{ehriPhase || lv.name} · {lv.grade}</div>
        </div>
        <p className="mb-4 font-finley-display text-lg text-finley-text">Does this seem right?</p>
        <div className="mx-auto flex max-w-sm flex-col gap-3">
          <motion.button {...tapBounce} onClick={() => onComplete(placedLevel, getQuizMeta(placedLevel))} className="min-h-[48px] rounded-finley-lg border-2 border-finley-success/30 bg-finley-success/10 px-5 py-3 font-finley-display text-base text-finley-text shadow-finley-sm">✅ Yes, that's about right</motion.button>
          <motion.button {...tapBounce} onClick={() => onComplete(Math.max(1, placedLevel - 2), getQuizMeta(Math.max(1, placedLevel - 2)))} className="min-h-[48px] rounded-finley-lg border-2 border-finley-primary/30 bg-finley-primary/10 px-5 py-3 font-finley-display text-base text-finley-text shadow-finley-sm">Too hard - go easier</motion.button>
          <motion.button {...tapBounce} onClick={() => onComplete(Math.min(15, placedLevel + 1), getQuizMeta(Math.min(15, placedLevel + 1)))} className="min-h-[48px] rounded-finley-lg border-2 border-finley-accent/40 bg-finley-accent/10 px-5 py-3 font-finley-display text-base text-finley-text shadow-finley-sm">Too easy - go harder</motion.button>
        </div>
      </motion.div>
    );
  }

  if (done && placedLevel) {
    const lv = LEVELS[placedLevel] || LEVELS[1];
    const levelName = getLevelName(placedLevel);
    const ehriPhase = getEhriPhase(placedLevel);
    return (
      <motion.div {...popIn} className="px-6 py-10 text-center">
        <div className="mb-3 text-7xl">🎯</div>
        <h2 className="mb-2 font-finley-display text-4xl text-finley-text">Great effort, {name}!</h2>
        <div className="mb-5 inline-block rounded-finley-lg border-2 px-8 py-3 shadow-finley-md" style={{ backgroundColor: `${lv.color}15`, borderColor: `${lv.color}44` }}>
          <div className="font-finley-display text-4xl" style={{ color: lv.color }}>{levelName}</div>
          <div className="text-base text-finley-text-light">{ehriPhase || lv.name} • {lv.grade}</div>
        </div>
        <div className="mt-2">
          <p className="mb-3 text-sm text-finley-text-light">Hand the device to a parent for a quick check.</p>
          <Btn onClick={() => setConfirmStep(true)} color={C.secondary}>Continue →</Btn>
        </div>
      </motion.div>
    );
  }

  if (!currentQuestion) {
    return (
      <motion.div {...fadeIn} className="px-6 py-10 text-center">
        <p className="text-finley-text-light">No quiz questions available right now.</p>
        {onCancel && <Btn onClick={onCancel}>Go Back</Btn>}
      </motion.div>
    );
  }

  return (
    <motion.div {...fadeIn} className="px-6 py-6 text-center">
      <div className="mb-3 flex items-center justify-between gap-3">
        {onCancel ? (
          <motion.button {...tapBounce} onClick={onCancel} className="min-h-[48px] rounded-finley-full bg-finley-bg-card px-4 py-2 text-sm text-finley-text-light shadow-finley-sm">
            ← Cancel
          </motion.button>
        ) : <span className="min-w-[48px]" />}
        <span className="rounded-finley-full bg-finley-primary/10 px-4 py-2 whitespace-nowrap text-sm font-semibold text-finley-primary">Adventure Check</span>
      </div>

      <SD current={progressDots} total={totalDots} />

      <p className="mb-2 text-xs text-finley-text-light">{config.instruction}</p>
      {encouragement && <p className="mb-2 font-finley-display text-sm text-finley-secondary">{encouragement}</p>}
      {guessPrompt && <p className="mb-2 text-sm text-finley-primary">{guessPrompt}</p>}

      <AnimatePresence mode="wait">
        <motion.div key={`${currentQuestion.id}-${questionSeed}`} {...slideUp} className="my-7">
          <p className="mb-5 font-finley-display text-3xl leading-tight text-finley-text">{currentQuestion.prompt}</p>
          <div className="mx-auto flex max-w-sm flex-col gap-3">
            {currentQuestion.options.map((opt, i) => (
              <motion.button
                key={i}
                {...(selected === null ? tapBounce : {})}
                onClick={() => selected === null && handleAnswer(i)}
                disabled={selected !== null}
                className="rounded-finley-lg border-2 border-finley-text-muted/20 bg-finley-bg-card px-5 py-4 font-finley-display text-xl text-finley-text shadow-finley-sm transition-colors"
                style={{ minHeight: config.minTapTarget }}
              >
                {opt}
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}
